import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Connection,
  EdgeChange,
  Handle,
  MarkerType,
  Node,
  NodeChange,
  useReactFlow,
} from '@xyflow/react'
import { useRolodexEdit } from 'features/fde'
import { useGeckoNotifications } from 'features/notifications'
import { useRolodexEntries } from 'features/rolodex'
import get from 'lodash/get'
import React, { createContext, useCallback, useEffect, useState } from 'react'
import {
  GeckoProcessFlowDiagram,
  PfdData,
  PfdEdge,
  PfdMetadata,
  PfdNode,
  RolodexEntryType,
} from 'types'

interface ProcessFlowDiagramBuilderContextProps {
  metadata: PfdMetadata
  setMetadata: React.Dispatch<React.SetStateAction<PfdMetadata>>
  processFlowEntry?: GeckoProcessFlowDiagram
  diagramID: string
  siteID: string
  pfd: PfdData
  setPfd: React.Dispatch<React.SetStateAction<PfdData>>
  clipboard: PfdNode | undefined
  setClipboard: React.Dispatch<React.SetStateAction<PfdNode | undefined>>
  undoActionStack: PfdData[]
  setUndoActionStack: React.Dispatch<React.SetStateAction<PfdData[]>>
  redoActionStack: PfdData[]
  addToUndoStackOnNewAction: (pfdState: PfdData) => void
  setRedoActionStack: React.Dispatch<React.SetStateAction<PfdData[]>>
  onNodeDragStop: () => void
  onNodeDragStart: () => void
  onNodesChange: (changes: NodeChange<PfdNode>[]) => void
  onEdgesChange: (changes: EdgeChange[]) => void
  onConnect: (params: Connection) => void
  onConnectEnd: (
    event: MouseEvent | TouchEvent,
    connectionState: {
      isValid: boolean | null
      fromHandle: Handle | null
      fromNode: Node | null
    }
  ) => void
  refreshProcessFlowData: () => Promise<void>
  handleSaveToRolodex: () => void
  displayPanel: boolean
  setDisplayPanel: React.Dispatch<React.SetStateAction<boolean>>
  inEditMode: boolean
  setInEditMode: React.Dispatch<React.SetStateAction<boolean>>
  editingEnabled: boolean
  onNodeClick?: (node: PfdNode) => void
  renderNodePopup?: (node: PfdNode, onClose: () => void) => React.ReactNode
  selectedNode: PfdNode | null
  setSelectedNode: React.Dispatch<React.SetStateAction<PfdNode | null>>
  selectedEdge: PfdEdge | null
  setSelectedEdge: React.Dispatch<React.SetStateAction<PfdEdge | null>>
}

export const ProcessFlowDiagramBuilderContext =
  createContext<ProcessFlowDiagramBuilderContextProps>(null!)

export const ProcessFlowDiagramBuilderDataLayer: React.FC<{
  children: React.ReactNode
  siteID: string
  diagramID: string
  viewOnly: boolean
  onNodeClick?: (node: PfdNode) => void
  renderNodePopup?: (node: PfdNode, onClose: () => void) => React.ReactNode
  selectedNodeByData?: {
    key: string
    value: string
  }
}> = (props) => {
  const {
    children,
    siteID,
    diagramID,
    viewOnly,
    onNodeClick,
    renderNodePopup,
    selectedNodeByData,
  } = props
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { rolodexPut } = useRolodexEdit()

  const [inEditMode, internalSetInEditMode] = useState(true)
  const [displayPanel, setDisplayPanel] = useState(true)
  const [selectedNode, setSelectedNode] = useState<PfdNode | null>(null)
  const [metadata, setMetadata] = useState<PfdMetadata>({ name: '' })
  const [selectedEdge, setSelectedEdge] = useState<PfdEdge | null>(null)
  const [pfd, setPfd] = useState<PfdData>({
    nodes: [],
    edges: [],
  })
  const [dragStartPfd, setDragStartPfd] = useState<PfdData>(pfd)
  const [clipboard, setClipboard] = useState<PfdNode>()
  const [undoActionStack, setUndoActionStack] = useState<PfdData[]>([])
  const [redoActionStack, setRedoActionStack] = useState<PfdData[]>([])
  const editingEnabled = !viewOnly && inEditMode

  const { processFlowDiagrams, forceRefresh: refreshData } = useRolodexEntries({
    ids: [diagramID],
    tags: {
      site: siteID,
    },
    limit: 1,
  })

  const processFlowEntry = processFlowDiagrams.at(0)
  const processFlowData = processFlowEntry?.data

  useEffect(() => {
    if (processFlowData?.diagram) {
      setPfd(processFlowData.diagram)
    }
    if (processFlowData?.metadata) {
      setMetadata(processFlowData.metadata)
    }
  }, [processFlowData])

  const addToUndoStackOnNewAction = useCallback((pfdState: PfdData) => {
    setUndoActionStack((prev) => {
      const newStack = [...prev, pfdState]
      return newStack.length > 20 ? newStack.slice(1) : newStack
    })
    setRedoActionStack([])
  }, [])

  const setInEditModeGuarded = useCallback(
    (next: React.SetStateAction<boolean>) => {
      if (viewOnly) return
      if (typeof next === 'function') {
        internalSetInEditMode((prev) =>
          (next as (prev: boolean) => boolean)(prev)
        )
      } else {
        internalSetInEditMode(next)
      }
    },
    [viewOnly]
  )

  const handleSaveToRolodex = useCallback(() => {
    if (!editingEnabled) return
    rolodexPut([
      {
        id: diagramID ?? '',
        type: RolodexEntryType.PROCESS_FLOW_DIAGRAM,
        data: {
          metadata: metadata ?? { name: '' },
          diagram: pfd,
        },
        tags: processFlowEntry?.tags ?? {},
      },
    ])
      .then(() => {
        successNotification('Process flow diagram saved successfully')
      })
      .catch(() => {
        errorNotification('Failed to save process flow diagram')
      })
  }, [
    diagramID,
    errorNotification,
    pfd,
    metadata,
    processFlowEntry?.tags,
    rolodexPut,
    successNotification,
    editingEnabled,
  ])

  const onNodeDragStart = useCallback(() => {
    setDragStartPfd(pfd)
  }, [pfd])

  const onNodeDragStop = useCallback(() => {
    addToUndoStackOnNewAction(dragStartPfd)
    setDragStartPfd(pfd)
  }, [pfd, addToUndoStackOnNewAction, setDragStartPfd, dragStartPfd])

  const onNodesChange = useCallback(
    (changes: NodeChange<PfdNode>[]) => {
      if (!editingEnabled) {
        return
      }
      setPfd((pfd) => {
        const updatedNodes = applyNodeChanges(changes, pfd.nodes)

        if (selectedNode) {
          const updatedSelectedNode = updatedNodes.find(
            (node) => node.id === selectedNode.id
          )
          if (updatedSelectedNode && updatedSelectedNode !== selectedNode) {
            setSelectedNode(updatedSelectedNode)
          }
        }

        return {
          ...pfd,
          nodes: updatedNodes,
        }
      })
    },
    [editingEnabled, setPfd, selectedNode, setSelectedNode]
  )
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      if (!editingEnabled) {
        return
      }
      setPfd((pfd) => ({
        ...pfd,
        edges: applyEdgeChanges(changes, pfd.edges),
      }))
    },
    [editingEnabled, setPfd]
  )

  const onConnect = useCallback(
    (params: Connection) => {
      if (!editingEnabled) return

      addToUndoStackOnNewAction(pfd)

      setPfd((pfd) => ({
        ...pfd,
        edges: addEdge(params, pfd.edges),
      }))
    },
    [editingEnabled, setPfd, addToUndoStackOnNewAction, pfd]
  )
  const { setNodes, setEdges, screenToFlowPosition } = useReactFlow()

  const onConnectEnd = useCallback(
    (
      event: MouseEvent | TouchEvent,
      connectionState: {
        isValid: boolean | null
        fromHandle: Handle | null
        fromNode: Node | null
      }
    ) => {
      const state = connectionState
      if (state.isValid) {
        return
      }

      addToUndoStackOnNewAction(pfd)

      const fromNodeId = state.fromNode?.id
      const fromHandleId = state.fromHandle?.id

      const id = `ghost-${Date.now()}`
      const { clientX, clientY } =
        'changedTouches' in event ? event.changedTouches[0] : event
      const newNode = {
        id,
        type: 'ghost',
        position: screenToFlowPosition({
          x: clientX,
          y: clientY,
        }),
        data: { label: '' },
      }

      const newEdge = {
        id: `${fromNodeId}-${id}`,
        source: fromNodeId ?? '',
        target: id,
        sourceHandle: fromHandleId,
        targetHandle: 'left',
        type: 'solidLine',
        data: { confirmedEdge: false },
        markerEnd: {
          type: MarkerType.Arrow,
        },
      }
      setNodes((nodes) => nodes.concat(newNode))
      setEdges((edges) => addEdge<PfdEdge>(newEdge, edges))
    },
    [setNodes, setEdges, screenToFlowPosition, addToUndoStackOnNewAction, pfd]
  )

  useEffect(() => {
    if (selectedNodeByData?.key) {
      const node = pfd.nodes.find((node) => {
        const nodeValue = get(node.data, selectedNodeByData.key)
        return (
          nodeValue !== undefined &&
          String(nodeValue) === selectedNodeByData.value
        )
      })
      if (node) {
        setSelectedNode(node)
      }
    }
  }, [selectedNodeByData, pfd.nodes])

  return (
    <ProcessFlowDiagramBuilderContext.Provider
      value={{
        metadata,
        setMetadata,
        processFlowEntry,
        diagramID: diagramID ?? '',
        siteID: siteID ?? '',
        pfd,
        setPfd,
        clipboard,
        setClipboard,
        undoActionStack,
        setUndoActionStack,
        redoActionStack,
        setRedoActionStack,
        addToUndoStackOnNewAction,
        onNodeDragStop,
        onNodeDragStart,
        onNodesChange,
        onEdgesChange,
        displayPanel,
        setDisplayPanel,
        onConnect,
        onConnectEnd,
        refreshProcessFlowData: refreshData,
        handleSaveToRolodex,
        inEditMode,
        setInEditMode: setInEditModeGuarded,
        editingEnabled,
        onNodeClick,
        renderNodePopup,
        selectedNode,
        setSelectedNode,
        selectedEdge,
        setSelectedEdge,
      }}>
      {children}
    </ProcessFlowDiagramBuilderContext.Provider>
  )
}
